import React from "react";
import { t } from "i18next";
import { detectDeviceOS, isInstagramBrowser, handlePaymentSuccessRedirection } from "lib/utils/helpers";
import { VELOCITY_ICON_URL, SNAPMINT_ICON_URL } from "lib/utils/constants";
import {
  PaymentGateways,
  PaymentGatewayImpl,
  MethodType,
  PaymentModeType,
  InitializePayment,
  RzpBaseOptions,
  RzpPaymentModes,
  CfDefaultPaymentModes,
  UnApplicablePaymentReasonType,
  PaymentMode,
  PaymentsResponse,
  PaymentModeAutomationPayloadForEvents,
  LoadSdkOnceParams,
} from "lib/types/payments";
import {
  loadRZPCustomSdk,
  loadRZPDefaultSDK,
  initializeRzpCustomPayment,
  initializeRzpDefaultPayment,
} from "lib/third-party/razorpay";
import {
  loadCfCustomSDK,
  loadCfDefaultSDK,
  initializeCfCustomPayment,
  initializeCfDefaultPayment,
} from "lib/third-party/cashfree";
import { loadPaytmDefaultSDK, initializePaytmDefaultPayment } from "lib/third-party/paytm";
import { PaymentModeUiConfigBE } from "lib/types/merchantConfigTypes";
import { initializeEasebuzzDefaultPayment, loadEasebuzzDefaultSdk } from "lib/third-party/easebuzz";
import { initializePayuDefaultPayment, loadPayUDefaultSdk } from "lib/third-party/payu";
import { initializePluralDefaultPayment, loadPluralDefaultSdk } from "lib/third-party/plural";
import { currencyFormatter } from "lib/utils/formatters";
import { CheckoutErrorType, ErrorType } from "lib/types/error";
import {
  Bank,
  CalendarDots,
  ClockClockwise,
  CreditCard,
  HandCoins,
  MoneyWavy,
  Wallet,
} from "@phosphor-icons/react";
import CustomError from "lib/core/customError";
import { CheckoutModalType } from "lib/types/checkout";
import { triggerSentryError } from "lib/utils/error";
import { withRetry } from "./network-helpers";
import { CheckoutMetadata } from "lib/types/merchantConfigTypes";
import { initializeCfV3Payment, loadV3cfSDK } from "lib/third-party/cashfreeV2";
import { PopupKey, PopupPayloadMap } from "lib/types/popup";
import { mergeOutOfStockItems } from "lib/utils/checkout";

const UpiIcons = React.lazy(() => import("assests/icons/UPIIcons"));

export type PaymentErrorHandlerCallbacks = {
  showErrorToast: (message: string) => void;
  getTranslation: (key: string) => string;
  setOOSItems?: (items: any[]) => void;
  setCheckoutModal?: (modal: CheckoutModalType | "OOS") => void;
  openPopup?: <K extends PopupKey>(key: K, payload: PopupPayloadMap[K]) => void;
  onOutOfStock?: (items: any[]) => void;
};

export type HandlePaymentErrorParams = {
  error: any;
  callbacks: PaymentErrorHandlerCallbacks;
  payload?: {
    checkoutId?: string;
    payment_mode?: string;
    payment_context?: string;
    [key: string]: any;
  };
  // Overrides for callers that are not a payment initiate (e.g. non-payment order placement)
  options?: {
    fallbackMessageKey?: string;
    errorUrl?: string;
    errorType?: ErrorType;
  };
};

export const parseInitiateError = (
  error: any,
): { code?: string; message?: string; items?: any[]; addons?: any[] } => {
  const data = error?.response?.data;
  const errField = data?.error;
  if (errField && typeof errField === "object") {
    return {
      code: errField.code,
      message: errField.message,
      items: errField.data?.items,
      addons: errField.data?.addons,
    };
  }
  return {
    code: errField,
    message: data?.message,
    items: data?.error_info?.metadata?.error_response?.metadata?.items,
    addons: data?.error_info?.metadata?.error_response?.metadata?.addons,
  };
};

const KRATOS_SHIPPING_ERROR_CODES = ["KR_813", "KR_814", "KR_815"];
const OUT_OF_STOCK_CODES = ["OUT_OF_STOCK", "KR_816"];

/**
 * Centralized error handler for payment operations
 * Handles both payment initiate errors and SDK load errors
 * Pass `options` to reuse it for other flows (e.g. non-payment order placement) with their own toast, URL and Sentry error type
 */
export const handlePaymentError = ({
  error,
  callbacks,
  payload,
  options,
}: HandlePaymentErrorParams): void => {
  const { showErrorToast, getTranslation, setOOSItems, setCheckoutModal, openPopup, onOutOfStock } =
    callbacks;

  const customError = new CustomError<CheckoutErrorType>(
    options?.errorType ? `${options.errorType} Error` : "Payment Initiate Timeout Error",
    {
      err: error,
      url: options?.errorUrl ?? `/checkout/v2/checkout/${payload?.checkoutId}/payments/initiate`,
      payload: { ...payload, type: options?.errorType ?? ErrorType.PAYMENT_INTITATE_TIMEOUT, error: error },
    },
  );

  const intiateFailureMessage = options?.fallbackMessageKey
    ? getTranslation(options.fallbackMessageKey)
    : getTranslation("payment_initiate_failed") || "Payment initiate failed";

  // timeout errors
  if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
    triggerSentryError(customError, options?.errorType ?? ErrorType.PAYMENT_INTITATE_TIMEOUT);
    showErrorToast(intiateFailureMessage);
    return;
  }

  //SDK errors
  if (error instanceof CustomError) {
    if (error?.extraData?.payload?.type === ErrorType.SDK_FAILURE) {
      const errorMessage = `${error?.extraData?.payload?.failedPg} SDK failed to load`;
      showErrorToast(errorMessage);
      triggerSentryError(error, ErrorType.SDK_FAILURE);
      return;
    }
  }

  const { code: errorCode, items: oosItems, addons: oosAddOns } = parseInitiateError(error);

  if (error?.response) {
    if (errorCode && OUT_OF_STOCK_CODES.includes(errorCode)) {
      const outOfStockItems = mergeOutOfStockItems(oosItems, oosAddOns);

      if (setOOSItems) {
        setOOSItems(outOfStockItems);
      }
      openPopup?.("OOS", {});
      onOutOfStock?.(outOfStockItems);
      return;
    }

    if (errorCode && KRATOS_SHIPPING_ERROR_CODES.includes(errorCode)) {
      openPopup?.("SHIPPING_HANDLES", {});
      return;
    }

    showErrorToast(intiateFailureMessage);
    triggerSentryError(customError, options?.errorType ?? ErrorType.PAYMENT_INITIATE_FAILED);
    return;
  }

  if (error?.message) {
    showErrorToast(error.message);
    return;
  }
};

export const handleKratosPaymentRedirect = (paymentResponse: any): boolean => {
  const actionUrl = paymentResponse?.next_steps?.pg_action?.action_url;
  if (!Boolean(actionUrl)) return false;
  handlePaymentSuccessRedirection(actionUrl, false);
  return true;
};

export const getUpiAppLink = (upiApp: string, intentLink: string) => {
  switch (upiApp.toUpperCase()) {
    case "PHONEPE":
      return intentLink.replace("upi", "phonepe");
    case "GPAY":
      return intentLink.replace("upi://", "tez://upi/");
    case "CRED":
      return intentLink.replace("upi://", "credpay://upi/");
    case "PAYTM":
      return detectDeviceOS() === "iOS"
        ? intentLink.replace("upi://", "paytm://upi/")
        : intentLink.replace("upi", "paytmmp");
    case "AMAZONPAY":
      return intentLink.replace("upi://", "amazonpay://upi/");
    case "POP":
      return intentLink.replace("upi://", "popclubapp://upi/");
    case "BHIM":
      return intentLink.replace("upi://", "bhim://upi/");
    case "NAVI":
      return intentLink.replace("upi://", "navipay://");
    default:
      return intentLink;
  }
};

const loadSdkOnce = ({
  pg,
  pgImpl,
  pgKey,
  pgVersion,
  pgAccountId,
  isRzpPref,
  isCFIframe,
}: LoadSdkOnceParams) => {
  switch (pg) {
    case "RP":
      return pgImpl === "CUSTOM" ? loadRZPCustomSdk(pgKey, pgAccountId, isRzpPref) : loadRZPDefaultSDK();
    case "CF":
      return isCFIframe
        ? loadV3cfSDK()
        : pgImpl === "CUSTOM"
        ? loadCfCustomSDK()
        : loadCfDefaultSDK(pgVersion);
    case "PAYTM":
      return loadPaytmDefaultSDK(pgKey);
    case "EASEBUZZ":
      return loadEasebuzzDefaultSdk(pgKey);
    case "PAYU":
      return loadPayUDefaultSdk();
    case "PLURAL":
      return loadPluralDefaultSdk();
    default:
      return Promise.resolve(true);
  }
};

export const loadPgSdk = async ({
  pg,
  pgImpl,
  pgKey,
  pgVersion,
  pgAccountId,
  isRzpPref,
  isCFIframe,
}: LoadSdkOnceParams) => {
  try {
    return await withRetry(
      () => loadSdkOnce({ pg, pgImpl, pgKey, pgVersion, pgAccountId, isRzpPref, isCFIframe }),
      3,
      500,
    );
  } catch (error: any) {
    throw new CustomError<CheckoutErrorType>("Failed to load PG SDK", {
      err: error,
      url: "",
      payload: { error, failedPg: pg, type: ErrorType.SDK_FAILURE },
    });
  }
};

export const getDialogHeader = (selectedPayment: PaymentModeType) => {
  if (!Boolean(selectedPayment)) {
    return;
  }
  const header = paymentMethodsConfig[selectedPayment]?.header;
  return header;
};

export const paymentMethodsConfig: MethodType = {
  UPI_COLLECT: {
    logo: UpiIcons,
    header: t("upi_payment_header"),
    subheader: t("upi_payment_subheader_apps_collect"),
    mappers: {
      RP: "upi",
      CF: "upi",
    },
  },
  UPI: {
    logo: UpiIcons,
    header: t("upi_payment_header"),
    subheader: t("upi_payment_subheader"),
    mappers: {
      RP: "upi",
      CF: "upi",
    },
  },
  UPI_INTENT: {
    logo: UpiIcons,
    header: t("upi_payment_header"),
    subheader: t("upi_payment_subheader_intent"),
    mappers: {
      RP: "upi",
      CF: "upi",
    },
  },
  CARD: {
    logo: CreditCard,
    header: t("card_payment_header"),
    subheader: t("card_payment_subheader"),
    mappers: {
      RP: "card",
      CF: "card",
    },
  },
  COD: {
    logo: HandCoins,
    header: t("cod_payment_header"),
    subheader: t("cod_payment_subheader"),
  },
  WALLET: {
    logo: Wallet,
    header: t("wallets_payment_header"),
    subheader: t("wallets_payment_subheader"),
    mappers: {
      RP: "wallet",
      CF: "app",
    },
  },
  NETBANKING: {
    logo: Bank,
    header: t("netbanking_payment_header"),
    subheader: t("netbanking_payment_subheader"),
    mappers: {
      RP: "netbanking",
      CF: "netbanking",
    },
  },
  EMI: {
    logo: CalendarDots,
    header: t("emi_payment_header"),
    subheader: t("emi_payment_subheader"),
    mappers: {
      RP: "emi",
      CF: "cardlessemi",
    },
  },
  // test
  SHOPIFY_GC: {
    logo: CreditCard,
    header: t("gift_card_payment_header"),
    subheader: t("gift_card_payment_subheader"),
  },
  CUSTOM_PAYMENT: {
    logo: "",
    header: "",
    subheader: "",
  },
  PAY_LATER: {
    logo: ClockClockwise,
    header: t("pay_later_payment_header"),
    subheader: t("pay_later_payment_subheader"),
    mappers: {
      RP: "paylater",
      CF: "paylater",
    },
  },
  SNPM: {
    logo: "https://cashfreelogo.cashfree.com/assets_images/pg/cardless/64/snapmint.png",
    header: t("snapmint_payment_header"),
    subheader: t("snapmint_payment_subheader"),
  },
  VELOCITY: {
    logo: VELOCITY_ICON_URL,
    header: t("velocity_payment_header"),
    subheader: t("velocity_payment_subheader"),
  },
  BHARATX: {
    logo: Bank,
    header: t("bharatx_payment_header"),
    subheader: t("bharatx_payment_subheader"),
  },
  CRED: {
    logo: CreditCard,
    header: t("cred_payment_header"),
    subheader: t("cred_payment_subheader"),
    mappers: {
      RP: "cred",
    },
  },
  CASHFREE: {
    logo: CreditCard,
    header: t("cashfree_payment_header"),
    subheader: t("cashfree_payment_subheader"),
  },
  GC: {
    logo: CreditCard,
    header: t("gift_card_payment_header"),
    subheader: t("gift_card_payment_subheader"),
  },
  PL: {
    logo: MoneyWavy,
    header: t("payment_link_header"),
    subheader: t("payment_link_subheader"),
  },
  LOYALTY: {
    logo: CreditCard,
    header: t("loyalty_payment_header"),
    subheader: t("loyalty_payment_subheader"),
  },
  PAYPAL: {
    logo: CreditCard,
    header: t("paypal_payment_header"),
    subheader: t("paypal_payment_subheader"),
    mappers: {
      RP: "paypal",
    },
  },
  OFFLINE_PAYMENT: {
    logo: CreditCard,
    header: t("online_payment_header"),
    subheader: t("online_payment_subheader"),
  },
  SPLIT_COD: {
    logo: HandCoins,
    header: t("split_cod_payment_header"),
    subheader: t("split_cod_payment_subheader"),
  },
};

export const initializePayment = async ({
  amount,
  pgOrderId,
  pg,
  pgImpl,
  pgRedirectUrl,
  pgAction,
  pgAccountId,
  paymentMode,
  userId,
  userName,
  userPhone,
  userEmail,
  checkoutId,
  merchantId,
  merchantName,
  currency = "INR",
  brandLogoUrl,
  brandThemeColor = "#5a31f4",
  onSuccessHandler,
  onFailureHandler,
  onCancelHandler,
  redirectUrl,
  methodPayload,
  floOrderReference,
  pgVersion,
  pgData,
  shouldRenderIframe,
}: InitializePayment) => {
  if (!Boolean(pg)) return;
  try {
    if (Boolean(pgAction === "redirect") && Boolean(pgRedirectUrl)) {
      // Use history.replace for VELOCITY so the checkout page is not kept in browser history,
      // preventing the user from navigating back into the payment flow after completion.
      const useReplace = pg === "VELOCITY";
      handlePaymentSuccessRedirection(pgRedirectUrl ?? "", false, useReplace);
      return;
    }

    switch (pg) {
      case "RP": {
        const baseOptions: RzpBaseOptions = {
          amount,
          rzpId: pgOrderId,
          merchantId,
          merchantName,
          method: findRzpPaymentMethodMapper(paymentMode),
          checkoutId,
          userPhone,
          userEmail,
          redirectUrl,
          ...(Boolean(pgAccountId) && { accountId: pgAccountId }),
        };
        if (pgImpl === "CUSTOM") {
          const pgCustomerId = paymentMode === "CARD" || paymentMode === "SAVED_MODES" ? userId : "";
          const response = await initializeRzpCustomPayment({
            ...baseOptions,
            userId: pgCustomerId,
            merchantName: merchantName,
            onFailureHandler: onFailureHandler,
            methodPayload,
          });
          return response;
        }

        const reponse = await initializeRzpDefaultPayment({
          ...baseOptions,
          brandLogoUrl,
          brandThemeColor,
          userName,
          onSuccessHandler,
        });
        return reponse;
      }
      case "SNPM": {
        (document as any).location.href = pgRedirectUrl;
        return;
      }
      case "CF": {
        if (shouldRenderIframe) {
          await initializeCfV3Payment({
            paymentSessionId: pgOrderId,
            paymentMode,
            onSuccessHandler,
            onFailureHandler,
            onCancelHandler,
          });
          break;
        }
        if (pgImpl === "CUSTOM") {
          const cfResponse = await initializeCfCustomPayment({
            order_token: pgOrderId,
            method: findCfPaymentMethodMapper(paymentMode),
            methodPayload: { ...methodPayload, ...{ phoneNumber: userPhone } },
          });
          const paymentRedirectUrl = cfResponse?.data?.url;
          if (Boolean(paymentRedirectUrl)) {
            handlePaymentSuccessRedirection(paymentRedirectUrl, false);
          }
          return;
        }
        if (pgAction === "redirect" && Boolean(pgRedirectUrl)) {
          // (document as any).location.href = pgRedirectUrl;
          handlePaymentSuccessRedirection(pgRedirectUrl ?? "", false);
          return;
        }
        initializeCfDefaultPayment({
          orderToken: pgOrderId,
          paymentMode: findCfPaymentMethodMapper(paymentMode),
          brandThemeColor: brandThemeColor,
          onSuccessHandler: onSuccessHandler,
          onFailureHandler: onFailureHandler,
          pgVersion: pgVersion,
        });
        break;
      }
      case "PAYU": {
        if (shouldRenderIframe && pgData) {
          initializePayuDefaultPayment({
            data: pgData,
            onSuccessHandler: onSuccessHandler,
            onFailureHandler: onFailureHandler,
            onCancelHandler: onCancelHandler,
          });
          return;
        }
        if (!pgRedirectUrl) return;
        handlePaymentSuccessRedirection(pgRedirectUrl ?? "", false);
        return;
      }
      case "PLURAL": {
        if (!pgRedirectUrl) return;
        // Pine's own SDK owns the modal + iframe (injected into document.body), and
        // reports the outcome via successHandler/failedHandler.
        initializePluralDefaultPayment({
          redirectUrl: pgRedirectUrl,
          onSuccessHandler,
          onFailureHandler,
        });
        return;
      }
      case "PHONEPE": {
        if (!pgRedirectUrl) return;
        handlePaymentSuccessRedirection(pgRedirectUrl ?? "", false);
        return;
      }
      case "PAYTM": {
        initializePaytmDefaultPayment({
          orderId: floOrderReference ?? "",
          token: pgOrderId,
          amount: amount,
          onCancelHandler,
        });
        return;
      }
      case "BHARATX": {
        handlePaymentSuccessRedirection(pgRedirectUrl ?? "", false);
        return;
      }
      case "EASEBUZZ": {
        initializeEasebuzzDefaultPayment({
          accessKey: pgOrderId,
          onSuccessHandler,
          onFailureHandler,
        });
        return;
      }
      case "SIMPL": {
        handlePaymentSuccessRedirection(pgRedirectUrl ?? "", false);
        return;
      }
      case "VELOCITY": {
        if (!pgRedirectUrl) return;
        handlePaymentSuccessRedirection(pgRedirectUrl, false, true);
        return;
      }
    }
  } catch (e: any) {
    throw new Error(e);
  }
};

export const findRzpPaymentMethodMapper = (mode: PaymentModeType): RzpPaymentModes => {
  const mappers = paymentMethodsConfig[mode]?.mappers;
  // @ts-ignore
  return mappers.RP;
};

export const findCfPaymentMethodMapper = (mode: PaymentModeType): CfDefaultPaymentModes => {
  const mappers = paymentMethodsConfig[mode]?.mappers;
  // @ts-ignore
  return mappers.CF;
};

export const paymentModeNextStep = (mode: PaymentModeType): "redirect" | "new_tab" => {
  if (mode === "PL") return "redirect";
  if (mode === "CASHFREE") return "redirect";
  return (window as any).ReactNativeWebView || isInstagramBrowser() || mode === "SNPM" || mode === "SIMPL"
    ? "redirect"
    : "new_tab";
};

export const isDefaultImplementationMode = (mode: PaymentModeType): boolean => {
  if (mode === "CASHFREE") return true;
  if (isInstagramBrowser() || mode === "PAY_LATER" || mode === "EMI") return true;
  return false;
};

export const filterPaymentMethods = (paymentMethods: any[], type: "AVAILABLE" | "UNAVAILABLE"): any[] => {
  return paymentMethods.filter((method) => {
    return type === "AVAILABLE" ? method.isAvailable : !method.isAvailable;
  });
};

export const getUnApplicablePaymentReason = (
  reasons: UnApplicablePaymentReasonType[],
  modeTitle: string,
): string => {
  if (!reasons || reasons.length === 0) return t("payment_mode_not_available");

  const hasDiscountsBlocker = reasons.find((e) => e.type === "INVALID_DISCOUNTS");
  if (hasDiscountsBlocker) {
    return t("payment_mode_not_available_discount_blocker");
  }

  const hasShippingBlocker = reasons.some((e) => e.type === "SHIPPING_HANDLES");
  if (hasShippingBlocker) {
    return t("payment_mode_not_available_shipping_blocker", { mode_title: modeTitle });
  }

  return t("payment_mode_not_available");
};

export const getPayButtonText = (modeData: PaymentMode) => {
  if (!modeData) return "";
  const mode = modeData?.name;
  if (mode === "COD") return t("cod_pay_x", { amount: currencyFormatter(modeData.amount) });
  if (mode === "SPLIT_COD")
    return t("split_cod_pay_amount", {
      current_amount: currencyFormatter(modeData.amount),
      cod_amount: currencyFormatter(modeData.metadata?.codAmount),
    });
  return t("pay_x_amount", { amount: currencyFormatter(modeData.amount) });
};

export const triggerSuccessEvent = () => {
  if (typeof window !== "undefined" && !window.localStorage.getItem("payment_success_flag")) {
    localStorage.setItem("payment_success_flag", "true");
  }
};
/**
 * Finds the "mode_specific_cart_item" value of the first payment method in the list of methods
 * where both "is_freebie" and "is_prepaid" are true in the "discount_metadata".
 *
 * @param {PaymentMode[]} data - An array of PaymentMode objects.
 * @returns {boolean} - True if match is found else false.
 */
export function findModeSpecificCartAlteration(data: PaymentMode[]): boolean {
  if (!data || !Array.isArray(data) || !Boolean(data?.length)) return false;
  for (const obj of data) {
    const discountMetadata = obj.modeDiscountMetadata;
    if (discountMetadata?.isFreebie && discountMetadata?.concessionAmount === 100) {
      return true;
    }
  }
  return false;
}

export const getPrepaidDiscountData = (
  paymentMethods: PaymentMode[],
): {
  maxDiscountMethod: PaymentMode | null;
  maxPrepaidDiscountText?: string | null;
  maxDiscountCoupon?: any;
  hasFreebie?: boolean;
  freebieMethod?: any;
} => {
  try {
    const allDiscounts = paymentMethods
      ?.filter((method) => {
        const discountMetadata = method.modeDiscountMetadata;
        return discountMetadata?.concessionAmount === 100;
      })
      ?.map((method) => {
        const discountMetadata = method.modeDiscountMetadata;
        return discountMetadata?.concessionAmount ?? 0;
      });

    const maxDiscountMethod = paymentMethods.reduce<PaymentMode | null>((max, method) => {
      const concession = method.modeDiscountMetadata?.concessionAmount ?? 0;
      const maxConcession = max?.modeDiscountMetadata?.concessionAmount ?? 0;
      return concession > maxConcession ? method : max;
    }, null);

    const freebieMethod = paymentMethods.find((method) => method.modeDiscountMetadata?.isFreebie) || null;

    const discountMetadata = maxDiscountMethod?.modeDiscountMetadata;
    const maxDiscountCoupon = discountMetadata?.concessionAmount ? discountMetadata : null;

    const discountType = maxDiscountCoupon?.deductionType;
    const discount = maxDiscountCoupon?.concessionAmount ?? 0;

    const discountTypeText = discountType === "FLAT" ? `₹${discount}` : `${discount}%`;
    const maxPrepaidDiscountText =
      allDiscounts?.length && Math.max(...allDiscounts) === Math.min(...allDiscounts)
        ? t("x_prepaid_discount", { amount: discountTypeText })
        : t("upto_x_discount", { amount: discountTypeText });

    return {
      maxDiscountMethod,
      maxDiscountCoupon,
      maxPrepaidDiscountText: Boolean(discount) ? maxPrepaidDiscountText : null,
      hasFreebie: Boolean(freebieMethod),
      freebieMethod,
    };
  } catch (e) {
    return {
      maxDiscountMethod: null,
    };
  }
};

type TileConfig = PaymentModeUiConfigBE | undefined;

const mergeSubscriptionTile = (sub: TileConfig, payOnce: TileConfig): TileConfig => {
  if (!sub) return payOnce;
  if (!payOnce) return sub;

  return {
    ...sub,
    title: sub.title || payOnce.title,
    subtitle: sub.subtitle || payOnce.subtitle,
    payment_card: {
      ...sub.payment_card,
      background_color: sub.payment_card?.background_color || payOnce.payment_card?.background_color,
      border_color: sub.payment_card?.border_color || payOnce.payment_card?.border_color,
      text_color: sub.payment_card?.text_color || payOnce.payment_card?.text_color,
      icon_color: sub.payment_card?.icon_color || payOnce.payment_card?.icon_color,
      subtitle_color: sub.payment_card?.subtitle_color || payOnce.payment_card?.subtitle_color,
    },
    offer_badge: {
      background_color: sub.offer_badge?.background_color || payOnce.offer_badge?.background_color,
      text_color: sub.offer_badge?.text_color || payOnce.offer_badge?.text_color,
    },
    suggested_tile: {
      background_color: sub.suggested_tile?.background_color || payOnce.suggested_tile?.background_color,
      text_color: sub.suggested_tile?.text_color || payOnce.suggested_tile?.text_color,
      title: sub.suggested_tile?.title || payOnce.suggested_tile?.title,
    },
    icons: sub.icons?.length ? sub.icons : payOnce.icons,
    mode_config: sub.mode_config ?? payOnce.mode_config,
  };
};

export const getUpiIntentModeConfig = (checkoutMetadata?: CheckoutMetadata, isSubscribeMode = false) => {
  const subscriptionTile = isSubscribeMode
    ? checkoutMetadata?.uiAttributes?.subscriptionPlanConfig?.paymentTiles?.find(
        (tile: any) => tile?.mode === "UPI_INTENT",
      )
    : undefined;

  const payOnceTile = checkoutMetadata?.uiAttributes?.paymentTileConfig?.modeDetails?.find(
    (tile: any) => tile?.mode === "UPI_INTENT",
  );

  return subscriptionTile?.mode_config ?? payOnceTile?.mode_config;
};

export const parseMethodUiAttributes = ({
  mode,
  checkoutMetadata,
  isSubscribeMode = false,
}: {
  mode?: PaymentMode;
  checkoutMetadata?: CheckoutMetadata;
  isSubscribeMode?: boolean;
}) => {
  const paymentTileConfigs = checkoutMetadata?.uiAttributes?.paymentTileConfig;
  const layoutUi = paymentTileConfigs?.layoutUI;
  const payOnceTileConfig = paymentTileConfigs?.modeDetails?.find((e: any) => e.mode === mode?.name);
  const subscriptionTileConfig = isSubscribeMode
    ? checkoutMetadata?.uiAttributes?.subscriptionPlanConfig?.paymentTiles?.find(
        (e: any) => e.mode === mode?.name,
      )
    : undefined;
  const paymentTileConfig = isSubscribeMode
    ? mergeSubscriptionTile(subscriptionTileConfig, payOnceTileConfig)
    : payOnceTileConfig;

  const velocityIconUrl = VELOCITY_ICON_URL;
  const snapmintIconUrl = SNAPMINT_ICON_URL;
  const baseIcons = paymentTileConfig?.icons?.map((icon: { url: string; name: string }) => icon?.url) || [];
  const savedIcons = paymentTileConfig?.icons;
  const icons =
    mode?.name === "VELOCITY"
      ? Array.isArray(savedIcons)
        ? baseIcons
        : [velocityIconUrl]
      : mode?.name === "SNPM"
      ? Array.isArray(savedIcons)
        ? baseIcons
        : [snapmintIconUrl]
      : baseIcons;

  return {
    mode: mode?.name ?? "",
    layout: paymentTileConfigs?.layout || "ICONS",
    modeTitle: mode?.metadata?.title || "",
    title: paymentTileConfig?.title || mode?.metadata?.title || "",
    subtitle: paymentTileConfig?.subtitle || mode?.metadata?.description || "",
    paymentCard: {
      backgroundColor:
        paymentTileConfig?.payment_card?.background_color ||
        layoutUi?.paymentCard?.backgroundColor ||
        "#FFFFFF",
      borderColor:
        paymentTileConfig?.payment_card?.border_color || layoutUi?.paymentCard?.borderColor || "#F0F0F0",
      textColor: paymentTileConfig?.payment_card?.text_color || layoutUi?.paymentCard?.textColor || "#4D4D4D",
      iconColor: paymentTileConfig?.payment_card?.icon_color || layoutUi?.paymentCard?.iconColor || "#4D4D4D",
      subtitleColor:
        paymentTileConfig?.payment_card?.subtitle_color || layoutUi?.paymentCard?.subtitleColor || "#949494",
      isSubtitleHidden:
        paymentTileConfig?.payment_card?.is_subtitle_hidden || mode?.metadata?.hideDescription || false,
      borderRadius:
        paymentTileConfig?.payment_card?.border_radius ?? layoutUi?.paymentCard?.borderRadius ?? 16,
    },
    offerBadge: {
      backgroundColor:
        paymentTileConfig?.offer_badge?.background_color ||
        layoutUi?.offerBadge?.backgroundColor ||
        "#F3F9F3",
      textColor: paymentTileConfig?.offer_badge?.text_color || layoutUi?.offerBadge?.textColor || "#2c874a",
    },
    isSuggested: paymentTileConfig?.is_suggested || false,
    suggestedTile: {
      backgroundColor: paymentTileConfig?.suggested_tile?.background_color || "#000000",
      textColor: paymentTileConfig?.suggested_tile?.text_color || "#FFFFFF",
      title: paymentTileConfig?.suggested_tile?.title || "Suggested payment method",
    },
    icons,
    modeConfig: paymentTileConfig?.mode_config,
    weight: paymentTileConfig?.weight ?? mode?.weight,
  };
};

export type AdditionalChargeType = "FLAT" | "PERCENTAGE";

export function getAdditionalChargeText(params: {
  mode: string;
  additionalChargeType?: AdditionalChargeType;
  additionalChargeValue?: number;
  rootLevelAdditionalChargeValue?: number;
  billing?: { cod?: number } | null;
  shouldShowAdditionalCharge: boolean;
  shouldShowCodCharge: boolean;
  codCharges: number | undefined;
}): string | null {
  const {
    mode,
    additionalChargeType = "FLAT",
    additionalChargeValue = 0,
    billing,
    shouldShowAdditionalCharge,
    shouldShowCodCharge,
    rootLevelAdditionalChargeValue,
    codCharges,
  } = params;

  if (mode === "SIMPL" || mode === "SIMPL_PL") {
    return t("includes_charges");
  }
  if ((mode === "SPLIT_COD" || mode === "COD") && shouldShowCodCharge) {
    return t("include_cod_fee", {
      amount: currencyFormatter(codCharges ?? 0, false),
    });
  }

  if (!rootLevelAdditionalChargeValue) return null;

  if (additionalChargeType === "FLAT" && shouldShowAdditionalCharge) {
    return t("include_additional_charges", {
      amount: currencyFormatter(additionalChargeValue),
    });
  }
  if (additionalChargeType === "FLAT" && shouldShowCodCharge) {
    return t("include_cod_fee", {
      amount: currencyFormatter((codCharges || 0) + additionalChargeValue, false),
    });
  }
  if (additionalChargeType === "PERCENTAGE" && shouldShowAdditionalCharge) {
    return t("include_additional_charges_in_percentage", {
      amount: additionalChargeValue,
    });
  }
  if (additionalChargeType === "PERCENTAGE" && shouldShowCodCharge) {
    return t("include_cod_fee_in_percentage", {
      amount: additionalChargeValue,
    });
  }
  return null;
}

export const parsePaymentModes = (modes: any): PaymentMode[] => {
  if (!modes || !Array.isArray(modes)) return [];
  return modes.map((mode) => ({
    name: mode?.name,
    weight: mode?.weight,
    amount: mode?.amount,
    orderAmount: mode?.order_amount,
    discount: mode?.discount,
    isAvailable: mode?.is_available,
    context: mode?.context,
    additionalCharges: mode?.additional_charges,
    codCharges: mode?.cod_charges,
    metadata: {
      title: mode?.metadata?.title,
      description: mode?.metadata?.description,
      minValue: mode?.metadata?.min_value,
      maxValue: mode?.metadata?.max_value,
      additionalChargeValue: mode.metadata?.additional_charge_value,
      additionalChargeType: mode.metadata?.additional_charge_type,
      codAmount: mode?.cod_split,
      hideDescription: mode.metadata?.hideDescription,
      logo: mode.metadata?.logo,
      ruleGroups: mode.metadata?.rule_groups?.map((group: any) => ({
        source: group?.source,
        failedRuleGroups: group?.failed_rule_groups,
        successfulRuleGroups: group?.successful_rule_groups,
      })),
      promptMetadata: {
        title: mode?.metadata?.prompt_metadata?.title ?? "",
        description: mode?.metadata?.prompt_metadata?.description ?? "",
      },
      excludeCodCharges: mode?.metadata?.exclude_cod_charges ?? false,
      modeConfig: mode?.metadata?.mode_config ?? {},
    },
    modeDiscountMetadata: {
      header: mode?.mode_discount_metadata?.header ?? "",
      isCustomHeader: mode?.mode_discount_metadata?.is_custom_header ?? "DEFAULT",
      deductionType: mode?.mode_discount_metadata?.deduction_type,
      concessionAmount: mode?.mode_discount_metadata?.concession_amount,
      maximumConcessionAmount: mode?.mode_discount_metadata?.maximum_concession_amount,
      freebies: mode?.mode_discount_metadata?.freebies,
      isFreebie: mode?.mode_discount_metadata?.is_freebie,
      freebieAmount: mode?.mode_discount_metadata?.freebie_amount || 0,
      modeSpecificCartAlteration: mode?.mode_discount_metadata?.mode_specific_cart_alteration,
    },

    unavailabilityReasons: mode?.unavailability_reasons ?? [],
    emiPlans: mode?.emi_plans?.map((plan: any) => ({
      tenure: plan?.tenure,
      downPayment: plan?.down_payment,
      remainingAmount: plan?.remaining_amount,
    })),
    ruleGroupMapping: mode?.rule_group_mapping
      ? {
          ruleGroups: {
            AUTOMATION: mode?.rule_group_mapping?.rule_groups?.AUTOMATION,
          },
        }
      : null,
  }));
};

export const parsePaymentsResponse = (data: any): PaymentsResponse => {
  if (!data) return { items: [], modes: [] };
  return {
    items: data.items || [],
    modes: parsePaymentModes(data.modes),
  };
};

export const getReviewAutomationPayload = (
  parsedModes: PaymentMode[],
): PaymentModeAutomationPayloadForEvents[] => {
  const automationPayload: PaymentModeAutomationPayloadForEvents[] = [];

  parsedModes?.forEach((method: PaymentMode) => {
    method?.ruleGroupMapping?.ruleGroups?.AUTOMATION?.forEach((automationId: string) => {
      if (!automationPayload.find((e) => e.id === automationId)) {
        automationPayload.push({ id: automationId, blockedModes: [], allowedModes: [] });
      }
    });
  });

  parsedModes?.forEach((method: PaymentMode) => {
    if (Boolean(method?.metadata?.ruleGroups?.length)) {
      method?.metadata?.ruleGroups?.forEach((ruleGroup: any) => {
        if (ruleGroup?.source === "AUTOMATION") {
          ruleGroup?.failedRuleGroups?.forEach((rule: string) => {
            automationPayload?.find((e) => e.id === rule)?.blockedModes?.push(method?.name);
          });
          ruleGroup?.successfulRuleGroups?.forEach((rule: string) => {
            automationPayload?.find((e) => e.id === rule)?.allowedModes?.push(method?.name);
          });
        }
      });
    }
  });

  return automationPayload;
};
